import React, { useState, useEffect, CSSProperties } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ProgressBar } from 'react-bootstrap'
import Alert from 'sweetalert-react'
import { getStorage, ref, getMetadata, getBlob } from 'firebase/storage'
import { getApp } from 'firebase/app'
import ImagePicker from './ImagePicker'
import { actionTypes, uploadFile, deleteFile } from 'src/actions/uploads.actions'
import { loadingSelector } from 'src/selectors/loading.selector'
import { handlerError } from 'src/selectors/error.selector'
import { selectCurrentCompany } from 'src/selectors/user.selector'

interface Src {
  url: string
  id?: number
  name?: string
}

interface AlertState {
  show?: boolean
  title: string
  onConfirm?: () => void
}

interface ImagePickerCloudProps {
  src?: Src | null
  onSuccessFile?: (url?: string, name?: string) => void
  imageType: { id: number; path: string }
  entityId?: number | string
  gallery?: number
  disabled?: boolean
  style?: CSSProperties
}

const ImagePickerCloud: React.FC<ImagePickerCloudProps> = ({
  src,
  onSuccessFile,
  imageType,
  entityId,
  gallery,
  disabled,
  style,
}) => {
  const dispatch = useDispatch()
  const company = useSelector(selectCurrentCompany)
  const uploadLoading = useSelector(state => loadingSelector([actionTypes.UPLOAD])(state))
  const deleteLoading = useSelector(state =>
    loadingSelector([actionTypes.DELETE_FILE])(state),
  )
  const [uploadProgress, setUploadProgress] = useState(0)
  const [alert, setAlert] = useState<AlertState>({ title: '' })
  const [suboptimal, setSuboptimal] = useState(false)

  useEffect(() => {
    setSuboptimal(false)
    if (!src?.name) return
    let cancelled = false
    getMetadata(ref(getStorage(getApp()), src.name))
      .then(meta => {
        if (!cancelled) setSuboptimal(Number(meta.size) > 150 * 1024)
      })
      .catch(() => {
        // archivo no existe o sin permisos — sin aviso
      })
    return () => {
      cancelled = true
    }
  }, [src?.name])

  const handleChange = (file: File | null) => {
    if (file) {
      const oldSrc = src
      dispatch(
        uploadFile({
          file,
          imageType,
          entityId,
          description: '',
          company,
          carousel: undefined,
          gallery,
          onProgress: (progress: number) => setUploadProgress(progress),
          onSuccess: (url: string, name: string) => {
            setUploadProgress(0)
            onSuccessFile?.(url, name)
            if (oldSrc?.name) {
              dispatch(
                deleteFile({
                  id: oldSrc.id,
                  name: oldSrc.name,
                  imageType,
                  entityId,
                  company,
                  carousel: undefined,
                  gallery,
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  onSuccess: () => {},
                }),
              )
            }
          },
          onError: () => {
            setUploadProgress(0)
            setAlert({
              ...handlerError(
                'Lo sentimos, no fue posible subir tu imagen en este momento',
              ),
              onConfirm: () => setAlert({ show: false, title: '' }),
            })
          },
        }),
      )
      return
    }

    if (entityId && src?.name) {
      dispatch(
        deleteFile({
          id: src.id,
          name: src.name,
          imageType,
          entityId,
          company,
          carousel: undefined,
          gallery,
          onSuccess: () => onSuccessFile?.(),
        }),
      )
    } else {
      onSuccessFile?.()
    }
  }

  return (
    <div>
      <ImagePicker
        value={src?.url ?? null}
        onChange={handleChange}
        disabled={disabled}
        deleteLoading={deleteLoading}
        suboptimal={suboptimal}
        onReoptimize={
          src?.name
            ? async () => {
                const name = src.name as string
                const blob = await getBlob(ref(getStorage(getApp()), name))
                const fileName = name.split('/').pop() ?? 'image.jpg'
                return new File([blob], fileName, { type: blob.type || 'image/jpeg' })
              }
            : undefined
        }
        style={style}
      />
      {uploadLoading && (
        <ProgressBar
          style={{ marginTop: 5 }}
          variant="success"
          now={uploadProgress}
          label={`${uploadProgress.toFixed(2)}%`}
        />
      )}
      <Alert {...alert} />
    </div>
  )
}

export default ImagePickerCloud
